import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { CONFIG } from "../config";

const LINKS = [
  { label: "Overview", href: "#overview" },
  { label: "Platinum", href: "#platinum" },
  { label: "Transparency", href: "#transparency" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header
      className="sticky top-0 z-50 w-full pointer-events-none"
      style={{ paddingTop: scrolled ? 10 : 16, transition: "padding-top 0.4s ease" }}
    >
      <div className="mx-auto max-w-[1080px] px-4 sm:px-6">
        <div
          className="pointer-events-auto relative flex items-center justify-between h-14 pl-4 pr-2 sm:pl-5 sm:pr-2.5 rounded-full"
          style={{
            backgroundColor: scrolled ? "rgba(13,14,14,0.78)" : "rgba(13,14,14,0.45)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: scrolled
              ? "1px solid var(--hairline-strong)"
              : "1px solid var(--hairline)",
            boxShadow: scrolled ? "0 10px 30px rgba(0,0,0,0.35)" : "none",
            transition:
              "background-color 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease",
          }}
        >
          <a href="#top" className="flex items-center gap-2.5 group" onClick={() => setMenuOpen(false)}>
            <span
              className="overflow-hidden rounded-full shrink-0 w-8 h-8"
              style={{ border: "1px solid var(--hairline)" }}
            >
              <img
                src="/logo-160.png"
                alt="Xylapay"
                className="w-full h-full object-cover"
              />
            </span>
            <span className="text-[15px] font-semibold text-[#F1F2EF] tracking-tight">
              {CONFIG.company}
            </span>
            <span className="hidden sm:inline w-px h-3.5 bg-[#F0F1EE]/15" />
            <span className="micro-label hidden sm:inline" style={{ marginTop: 1 }}>
              {CONFIG.token}
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-[13.5px] text-[#A0A5A2] hover:text-[#F1F2EF] transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <a
              href={CONFIG.buyUrl || "#market"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F0F1EE] px-4 py-2 text-[13px] font-medium text-[#080909] hover:bg-white transition-colors duration-200"
            >
              Buy {CONFIG.token}
              <span className="text-[12px]">↗</span>
            </a>
            <button
              type="button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden flex items-center justify-center w-11 h-11 rounded-full text-[#F1F2EF] hover:bg-[#1a1c1c] transition-colors duration-200"
            >
              {menuOpen ? <X size={19} strokeWidth={1.75} /> : <Menu size={19} strokeWidth={1.75} />}
            </button>
          </div>

          {/* Mobile dropdown menu */}
          <div
            className="md:hidden absolute left-0 right-0 top-[calc(100%+8px)] pointer-events-auto origin-top"
            style={{
              opacity: menuOpen ? 1 : 0,
              transform: menuOpen ? "translateY(0) scaleY(1)" : "translateY(-6px) scaleY(0.98)",
              pointerEvents: menuOpen ? "auto" : "none",
              transition: "opacity 0.25s ease, transform 0.25s ease",
            }}
          >
            <div
              className="rounded-2xl overflow-hidden p-2"
              style={{
                backgroundColor: "rgba(13,14,14,0.94)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: "1px solid var(--hairline-strong)",
                boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
              }}
            >
              {LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-3 rounded-xl text-[15px] text-[#D5D8D5] hover:bg-[#1a1c1c] hover:text-[#F1F2EF] transition-colors duration-200"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
